import { useState, useEffect, useCallback } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { fetchApi } from '../../lib/api';

export default function GmailConnect() {
  const { user } = useAuth();
  const [connection, setConnection] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkConnection = useCallback(async () => {
    if (!user) { setLoading(false); return; }
    const { data } = await supabase
      .from('gmail_connections')
      .select('gmail_email')
      .eq('user_id', user.id)
      .maybeSingle();
    setConnection(data);
    setLoading(false);
  }, [user]);

  useEffect(() => { checkConnection(); }, [checkConnection]);

  // Check for redirect params
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('gmail') === 'connected') {
      checkConnection();
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, [checkConnection]);

  const connectGmail = async () => {
    try {
      const res = await fetchApi('/api/auth/gmail');
      if (res.url) window.location.href = res.url;
    } catch (err) {
      alert('Failed to start Gmail connection: ' + err.message);
    }
  };

  const disconnectGmail = async () => {
    if (!user) return;
    await supabase.from('gmail_connections').delete().eq('user_id', user.id);
    setConnection(null);
  };

  if (loading) {
    return (
      <div className="glass p-7 max-w-2xl">
        <h2 className="font-heading text-xl text-vault-navy mb-5">Gmail Integration</h2>
        <div className="text-sm text-gray-400">Checking connection...</div>
      </div>
    );
  }

  return (
    <div className="glass p-7 max-w-2xl">
      <h2 className="font-heading text-xl text-vault-navy mb-5">Gmail Integration</h2>

      {connection ? (
        <div className="space-y-4">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-sm font-medium">
              <span className="w-2 h-2 rounded-full bg-emerald-500" />
              Connected
            </span>
          </div>
          <div>
            <label className="block text-[12px] font-semibold text-gray-400 uppercase tracking-wider">Gmail Account</label>
            <div className="text-vault-navy text-sm mt-1 font-medium">{connection.gmail_email}</div>
          </div>
          <button
            onClick={disconnectGmail}
            className="mt-2 px-5 py-2.5 rounded-btn text-sm font-semibold text-vault-error border border-vault-error/30 hover:bg-red-50 transition-colors"
          >
            Disconnect Gmail
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          <p className="text-sm text-gray-500">
            Connect your Gmail account to send event invitations directly from PlayVentr.
          </p>
          <button
            onClick={connectGmail}
            className="px-6 py-2.5 rounded-btn text-sm font-semibold text-white shadow-btn transition-all hover:shadow-lg"
            style={{ background: 'linear-gradient(135deg, #c9a84c, #F59E0B)' }}
          >
            Connect Gmail
          </button>
          <p className="text-[11px] text-gray-400 italic">
            Used for sending event invitations only
          </p>
        </div>
      )}
    </div>
  );
}
